import { memo } from "react";
import clsx from "clsx";
import type { SuitName, ValueName } from "../../lib/types";

// ---------------------------------------------------------------------------
// A hand-drawn French-suited playing card. No emoji, no images: pure SVG.
// - Number cards (2..10): authentic pip layouts.
// - Face cards (J/Q/K): framed serif monogram + small suit.
// - Ace: large centered suit glyph; the Ace of Diamonds played "high" wears a
//   small crown above the suit (RULE-003, ADR-011 visual cue).
// ViewBox is 100x140 (aspect ~5:7); the consumer sets pixel size via `size`.
// ---------------------------------------------------------------------------

const SUIT_GLYPH: Record<SuitName, string> = {
  SPADES: "♠",
  CLUBS: "♣",
  HEARTS: "♥",
  DIAMONDS: "♦",
};

const SUIT_COLOR: Record<SuitName, string> = {
  SPADES: "#14110c",
  CLUBS: "#14110c",
  HEARTS: "#B23A2E",
  DIAMONDS: "#B23A2E",
};

const VALUE_INDEX: Record<ValueName, string> = {
  ACE: "A",
  TWO: "2",
  THREE: "3",
  FOUR: "4",
  FIVE: "5",
  SIX: "6",
  SEVEN: "7",
  JACK: "J",
  QUEEN: "Q",
  KING: "K",
};

// Pip layouts (normalized 0..1 coords; x=column, y=row). The 40-card Italian
// deck has number cards only for TWO..SEVEN (no 8/9/10).
const PIPS: Partial<Record<ValueName, Array<[number, number]>>> = {
  TWO: [[0.5, 0.2], [0.5, 0.8]],
  THREE: [[0.5, 0.2], [0.5, 0.5], [0.5, 0.8]],
  FOUR: [[0.3, 0.2], [0.7, 0.2], [0.3, 0.8], [0.7, 0.8]],
  FIVE: [[0.3, 0.2], [0.7, 0.2], [0.5, 0.5], [0.3, 0.8], [0.7, 0.8]],
  SIX: [[0.3, 0.2], [0.7, 0.2], [0.3, 0.5], [0.7, 0.5], [0.3, 0.8], [0.7, 0.8]],
  SEVEN: [[0.3, 0.2], [0.7, 0.2], [0.5, 0.35], [0.3, 0.5], [0.7, 0.5], [0.3, 0.8], [0.7, 0.8]],
};

type Size = "sm" | "md" | "lg";
const SIZE_PX: Record<Size, number> = { sm: 50, md: 74, lg: 104 };

interface Props {
  value: ValueName;
  suit: SuitName;
  playedAsHigh?: boolean;
  size?: Size;
  className?: string;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

function CornerIndex({ value, suit, color }: { value: string; suit: string; color: string }) {
  return (
    <g fill={color} fontFamily="Georgia, serif" textAnchor="middle">
      <text x="14" y="22" fontSize="16" fontWeight="700">{value}</text>
      <text x="14" y="35" fontSize="12">{suit}</text>
      <g transform="rotate(180 50 70)">
        <text x="14" y="22" fontSize="16" fontWeight="700">{value}</text>
        <text x="14" y="35" fontSize="12">{suit}</text>
      </g>
    </g>
  );
}

function Pip({ x, y, glyph, color }: { x: number; y: number; glyph: string; color: string }) {
  // Lower pips are rotated 180° (authentic). y > 0.5 => flip.
  const flip = y > 0.5 ? 180 : 0;
  return (
    <g transform={`rotate(${flip} ${x * 100} ${y * 140})`}>
      <text
        x={x * 100}
        y={y * 140 + 5}
        fontSize="18"
        fill={color}
        fontFamily="Georgia, serif"
        textAnchor="middle"
      >
        {glyph}
      </text>
    </g>
  );
}

function FaceCard({ value, glyph, color }: { value: string; glyph: string; color: string }) {
  return (
    <g>
      <rect x="22" y="40" width="56" height="60" rx="6" fill={color} opacity="0.07" />
      <rect x="22" y="40" width="56" height="60" rx="6" fill="none" stroke={color} strokeWidth="0.6" opacity="0.35" />
      <text
        x="50"
        y="82"
        fontSize="40"
        fontFamily="'Fraunces', Georgia, serif"
        fontWeight="600"
        fill={color}
        textAnchor="middle"
      >
        {value}
      </text>
      <text x="50" y="98" fontSize="12" fill={color} fontFamily="Georgia, serif" textAnchor="middle" opacity="0.6">
        {glyph}
      </text>
    </g>
  );
}

function AceCard({ suit, color, isHigh }: { suit: string; color: string; isHigh: boolean }) {
  return (
    <g>
      {isHigh && (
        <g fill={color} textAnchor="middle">
          {/* small crown above the ace for the "high" Asso di Quadri */}
          <path
            d="M40 36 L44 28 L50 33 L56 28 L60 36 Z"
            fill={color}
            opacity="0.9"
          />
        </g>
      )}
      <text
        x="50"
        y="88"
        fontSize="58"
        fill={color}
        fontFamily="Georgia, serif"
        textAnchor="middle"
      >
        {suit}
      </text>
    </g>
  );
}

export const PlayingCard = memo(function PlayingCard({
  value,
  suit,
  playedAsHigh = false,
  size = "md",
  className,
  selected,
  disabled,
  onClick,
}: Props) {
  const w = SIZE_PX[size];
  const h = (w * 140) / 100;
  const glyph = SUIT_GLYPH[suit];
  const color = SUIT_COLOR[suit];
  const index = VALUE_INDEX[value];
  const pips = PIPS[value];

  const interactive = !!onClick && !disabled;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${index} of ${suit.toLowerCase()}${playedAsHigh ? " (high)" : ""}`}
      className={clsx(
        "relative shrink-0 transition-transform duration-150",
        interactive && "cursor-pointer hover:-translate-y-2",
        selected && "-translate-y-3",
        disabled && "opacity-60 cursor-not-allowed",
        className,
      )}
      style={{ width: w, height: h }}
    >
      <svg
        viewBox="0 0 100 140"
        width={w}
        height={h}
        className="drop-shadow-card rounded-card"
        style={{ display: "block" }}
      >
        {/* face */}
        <rect x="0" y="0" width="100" height="140" rx="9" fill="#F6F1E4" />
        <rect x="0" y="0" width="100" height="140" rx="9" fill="none" stroke="#1d2719" strokeWidth="0.8" opacity="0.18" />
        <rect x="2.5" y="2.5" width="95" height="135" rx="7" fill="none" stroke={color} strokeWidth="0.4" opacity="0.18" />

        <CornerIndex value={index} suit={glyph} color={color} />

        {value === "ACE" && <AceCard suit={glyph} color={color} isHigh={playedAsHigh} />}
        {value === "JACK" && <FaceCard value="J" glyph={glyph} color={color} />}
        {value === "QUEEN" && <FaceCard value="Q" glyph={glyph} color={color} />}
        {value === "KING" && <FaceCard value="K" glyph={glyph} color={color} />}
        {pips && pips.map(([px, py], i) => (
          <Pip key={i} x={px} y={py} glyph={glyph} color={color} />
        ))}
      </svg>
    </button>
  );
});
